import { supabase } from "@/integrations/supabase/client";

/**
 * Supportsessioner: läsläge är alltid tillgängligt för systemadmin, skrivläge
 * kräver att projektägaren aktivt godkänner en tidsbegränsad session.
 */

export type SupportMode = "read" | "write";
export type SupportStatus = "pending" | "active" | "expired" | "revoked" | "denied";

export interface SupportSession {
  id: string;
  project_id: string | null;
  target_user_id: string;
  support_user_id: string;
  mode: SupportMode;
  status: SupportStatus;
  reason: string | null;
  expires_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupportAccessLogRow {
  id: string;
  session_id: string | null;
  project_id: string | null;
  support_user_id: string;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

export const SUPPORT_STATUS_LABELS: Record<SupportStatus, string> = {
  pending: "Väntar på godkännande",
  active: "Aktiv",
  expired: "Utgången",
  revoked: "Återkallad",
  denied: "Nekad",
};

export const isSessionLive = (session: SupportSession) =>
  session.status === "active" &&
  (!session.expires_at || new Date(session.expires_at).getTime() > Date.now());

/** Startar en session. Läsläge aktiveras direkt, skrivläge kräver godkännande. */
export async function requestSupportSession(input: {
  projectId: string;
  targetUserId: string;
  supportUserId: string;
  mode: SupportMode;
  reason?: string;
  hours?: number;
}): Promise<SupportSession> {
  const hours = input.hours ?? 8;
  const expiresAt = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from("support_sessions")
    .insert({
      project_id: input.projectId,
      target_user_id: input.targetUserId,
      support_user_id: input.supportUserId,
      mode: input.mode,
      status: input.mode === "read" ? "active" : "pending",
      reason: input.reason ?? null,
      expires_at: expiresAt,
      approved_at: input.mode === "read" ? new Date().toISOString() : null,
    })
    .select()
    .single();
  if (error) throw error;
  return data as SupportSession;
}

export async function listSupportSessions(filter?: {
  projectId?: string;
  targetUserId?: string;
}): Promise<SupportSession[]> {
  let query = supabase
    .from("support_sessions")
    .select("*")
    .order("created_at", { ascending: false });
  if (filter?.projectId) query = query.eq("project_id", filter.projectId);
  if (filter?.targetUserId) query = query.eq("target_user_id", filter.targetUserId);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as SupportSession[];
}

/** Sessioner som väntar på den inloggade användarens godkännande. */
export async function listPendingApprovals(userId: string): Promise<SupportSession[]> {
  const { data, error } = await supabase
    .from("support_sessions")
    .select("*")
    .eq("target_user_id", userId)
    .eq("status", "pending")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as SupportSession[];
}

export async function decideSupportSession(
  sessionId: string,
  decision: "approved" | "denied",
  userId: string,
) {
  const { error } = await supabase
    .from("support_sessions")
    .update({
      status: decision === "approved" ? "active" : "denied",
      approved_at: decision === "approved" ? new Date().toISOString() : null,
      approved_by: userId,
    })
    .eq("id", sessionId);
  if (error) throw error;
}

export async function revokeSupportSession(sessionId: string) {
  const { error } = await supabase
    .from("support_sessions")
    .update({ status: "revoked", revoked_at: new Date().toISOString() })
    .eq("id", sessionId);
  if (error) throw error;
}

/** Aktiv session för ett visst projekt där jag är supportanvändare. */
export async function getMyActiveSession(
  projectId: string,
  supportUserId: string,
): Promise<SupportSession | null> {
  const { data, error } = await supabase
    .from("support_sessions")
    .select("*")
    .eq("project_id", projectId)
    .eq("support_user_id", supportUserId)
    .eq("status", "active")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  const session = (data as SupportSession) ?? null;
  return session && isSessionLive(session) ? session : null;
}

export async function logSupportAccess(input: {
  sessionId?: string | null;
  projectId?: string | null;
  supportUserId: string;
  action: string;
  resourceType?: string;
  resourceId?: string;
  details?: Record<string, unknown>;
}) {
  try {
    await supabase.from("support_access_log").insert({
      session_id: input.sessionId ?? null,
      project_id: input.projectId ?? null,
      support_user_id: input.supportUserId,
      action: input.action,
      resource_type: input.resourceType ?? null,
      resource_id: input.resourceId ?? null,
      details: (input.details ?? {}) as never,
    });
  } catch (error) {
    console.error("Kunde inte logga supportåtkomst:", error);
  }
}

export async function listSupportLog(sessionId: string): Promise<SupportAccessLogRow[]> {
  const { data, error } = await supabase
    .from("support_access_log")
    .select("*")
    .eq("session_id", sessionId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as SupportAccessLogRow[];
}

/**
 * Loggar en visning i spegelläget serverside. Databasen kontrollerar att
 * sessionen finns, tillhör den inloggade och fortfarande är aktiv – annars
 * kastas ett fel och vyn stängs.
 */
export async function logSupportView(input: {
  sessionId: string;
  resourceType: string;
  resourceId?: string | null;
  details?: Record<string, unknown>;
}): Promise<void> {
  const { error } = await supabase.rpc("support_log_view", {
    p_session_id: input.sessionId,
    p_resource_type: input.resourceType,
    p_resource_id: input.resourceId ?? null,
    p_details: (input.details ?? {}) as never,
  });
  if (error) throw error;
}

/**
 * Måste anropas innan en ändring görs i supportläge. Databasen nekar om det
 * inte finns en godkänd, ej utgången session i skrivläge – och loggar försöket.
 */
export async function guardSupportWrite(input: {
  sessionId: string;
  resourceType?: string;
  resourceId?: string;
}): Promise<void> {
  const { error } = await supabase.rpc("support_write_guard", {
    p_session_id: input.sessionId,
    p_resource_type: input.resourceType ?? null,
    p_resource_id: input.resourceId ?? null,
  });
  if (error) throw error;
}
